import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Warning } from '@/assets/icons';
import { Accordion } from '@common/components/Accordion';
import { theme } from '@/modules/common/theme';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { VehicleType } from '@/modules/simulation/components/EditPanel/components/VehicleType';
import { rangeWithInterval } from '@/modules/common/helpers/array';
import { VehicleTypeRange } from '@/modules/simulation/helpers/customTypes';

type Props = {
  initialRanges: VehicleTypeRange[];
  onChange: (ranges: VehicleTypeRange[], changedRange: VehicleTypeRange) => void;
};

export const VehicleTypes = ({ initialRanges, onChange }: Props) => {
  const { t } = useTranslation();
  const { allVehicles } = useVehicleStore();
  const { getVehicleTypes } = useFloorplanDesignStore();

  const vehicleTypes = getVehicleTypes();

  const [ranges, setRanges] = useState<VehicleTypeRange[]>(initialRanges);

  const onRangeChange = useCallback(
    (vtRange: VehicleTypeRange) => {
      let updatedRanges: VehicleTypeRange[] = [];
      ranges.forEach((item) => {
        updatedRanges.push({
          ...item,
          range: {
            ...item.range,
            from: item.range.to,
          },
        });
      });

      updatedRanges = updatedRanges.map((item) =>
        item.name === vtRange.name ? { ...vtRange } : item,
      );

      setRanges(updatedRanges);
      onChange(updatedRanges, vtRange);
    },
    [ranges, onChange],
  );

  const hasMissingVehicles = useMemo(
    () =>
      vehicleTypes.some(
        (item1) => allVehicles.getAll().find((item2) => item1.id === item2.name) === null,
      ),
    [vehicleTypes, allVehicles],
  );

  const rangeDisplayValue = useMemo(() => {
    const vehiclesRange = ranges
      .map((item) => {
        const rangeArray = rangeWithInterval(item.range.from, item.range.to, item.range.step) || [
          0,
        ];
        return { least: rangeArray[0], most: rangeArray[rangeArray.length - 1] };
      })
      .reduce(
        (acc, vehicleAmount) => {
          acc.least += vehicleAmount.least;
          acc.most += vehicleAmount.most;

          return acc;
        },
        { least: 0, most: 0 },
      );

    const displayValue =
      vehiclesRange.least === vehiclesRange.most
        ? vehiclesRange.least
        : `${vehiclesRange.least} - ${vehiclesRange.most}`;

    return displayValue;
  }, [ranges]);

  const onAccordionToggle = useCallback((expanded: boolean) => !expanded, []);

  if (!vehicleTypes?.length) {
    return null;
  }

  return (
    <Accordion
      title={t('interface:simulation.simulation_edit.vehicle_types.header', {
        range: rangeDisplayValue,
      })}
      afterIcon={
        hasMissingVehicles && (
          <Warning
            style={{
              marginRight: '12px',
              fill: theme.palette.error.main,
            }}
          />
        )
      }
      onChange={onAccordionToggle}
      sx={{ px: 2, overflow: 'hidden' }}
    >
      <Stack
        sx={{
          flex: '1 1 auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {ranges.map((vtRange) => {
          const vehicle = allVehicles.getAll().find((item) => item.name === vtRange.name);

          if (!vehicle) return null;
          return (
            <VehicleType
              key={vtRange.name}
              vtRange={vtRange}
              imageUrl={vehicle.image.url}
              onRangeChange={onRangeChange}
            />
          );
        })}
      </Stack>
    </Accordion>
  );
};
